import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../components/toast-container';
import { updateEGiftCard as updateEGiftCardRequest } from '../api/eGiftCard.api';
import type { UpdateEGiftCardPayload } from '../types/eGiftCard.types';
import type { CreateEGiftCardRequest } from '../types/eGiftCardForm.types';
import { toIsoStringWithOffset } from '../utils/eGiftCardDateTime';
import { toEGiftCardUpdateItems } from '../utils/eGiftCardRequestMapper';

interface UseUpdateEGiftCardReturn {
    updateEGiftCard: (eGiftCardId: string, data: CreateEGiftCardRequest) => Promise<boolean>;
    isLoading: boolean;
    isError: boolean;
    error: string | null;
}

export const useUpdateEGiftCard = (): UseUpdateEGiftCardReturn => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const updateEGiftCard = useCallback(async (
        eGiftCardId: string,
        data: CreateEGiftCardRequest,
    ): Promise<boolean> => {
        setIsLoading(true);
        setError(null);

        try {
            const request: UpdateEGiftCardPayload = {
                merchant_brand_id: data.merchant_brand_id,
                gift_cards: toEGiftCardUpdateItems(data.gift_cards),
                valid_from: toIsoStringWithOffset(data.valid_from, data.timezone),
                valid_to: toIsoStringWithOffset(data.valid_to, data.timezone),
                timezone: data.timezone,
                is_alert_low_stock: data.is_alert_low_stock,
            };

            const response = await updateEGiftCardRequest(eGiftCardId, request);

            if (!response.success) {
                const message = response.message || 'Failed to update gift card reward';
                setError(message);
                addToast({
                    type: 'warning',
                    title: 'Warning!',
                    message,
                });
                return false;
            }

            addToast({
                type: 'success',
                title: 'Success!',
                message: 'Gift card reward has been updated successfully',
            });
            navigate('/e-gift-card');
            return true;
        } catch (caughtError: unknown) {
            console.error('Error updating gift card:', caughtError);
            const message = 'An error occurred while updating the gift card reward';
            setError(message);
            addToast({
                type: 'error',
                title: 'Error!',
                message,
            });
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [addToast, navigate]);

    return {
        updateEGiftCard,
        isLoading,
        isError: error !== null,
        error,
    };
};
